
import { CartProduct, TaxInfo } from './types';
import { 
  getCartCount, 
  getShippingCostSync,
  calculateCartTotalWithPricing
} from '@/utils/cartCalculations';
import { calculateTaxWithStripe } from '@/utils/taxCalculation';

export const useCartCalculations = (
  cartItems: CartProduct[],
  discount: number,
  appliedCredit: number,
  getProductPrice: (productId: number) => number,
  shippingZipCode: string = '',
  giftTotal: number = 0
) => {
  const calculateCartTotal = () => {
    // Use dynamic pricing for cart total calculation
    const total = calculateCartTotalWithPricing(cartItems, getProductPrice);
    console.log('Cart total calculated with dynamic pricing:', total);
    return total;
  };
  
  const calculateCartCount = () => getCartCount(cartItems);
  
  const calculateShippingCost = () => {
    // Use synchronous version for immediate UI updates with current pricing
    const shipping = getShippingCostSync(cartItems, getProductPrice);
    console.log('Shipping cost calculated with current prices:', shipping);
    return shipping;
  };
  
  const calculateDiscount = () => {
    console.log('getDiscount called, returning:', discount);
    return discount;
  };
  
  const calculateCreditDiscount = () => {
    console.log('getCreditDiscount called, returning:', appliedCredit);
    return appliedCredit;
  };
  
  const calculateTaxInfo = async (): Promise<TaxInfo> => {
    const subtotal = calculateCartTotal();
    const discountedSubtotal = Math.max(0, subtotal - discount);
    console.log('Calculating tax for:', { subtotal, discountedSubtotal, shippingZipCode });
    
    if (!shippingZipCode) {
      console.log('No shipping zip code provided');
      return { taxAmount: 0, taxRate: 0, stateName: '' };
    }
    
    try {
      // Use Stripe Tax API with shipping address
      const taxInfo = await calculateTaxWithStripe(discountedSubtotal, {
        postal_code: shippingZipCode,
        country: 'US'
      });
      console.log('Tax calculation result:', taxInfo);
      return taxInfo;
    } catch (error) {
      console.error('Tax calculation failed:', error);
      return { taxAmount: 0, taxRate: 0, stateName: 'Error' };
    }
  };
  
  const calculateTaxAmount = async () => {
    const taxInfo = await calculateTaxInfo();
    return taxInfo.taxAmount;
  };

  const calculateFinalTotal = () => {
    const subtotal = calculateCartTotal();
    const shippingCost = calculateShippingCost();
    const promoDiscount = discount;
    const creditDiscount = appliedCredit;
    
    // Note: Tax is now handled separately through the useTaxCalculation hook
    // This is now a synchronous calculation without tax
    const finalTotal = Math.max(0, subtotal + giftTotal + shippingCost - promoDiscount - creditDiscount);
    
    console.log('Partial final total calculation (without tax):', { 
      subtotal, 
      shippingCost,
      promoDiscount, 
      creditDiscount,
      finalTotal
    });
    
    return finalTotal;
  };

  // Cart-specific final total that never includes tax
  const calculateCartFinalTotal = () => {
    const subtotal = calculateCartTotal();
    const shippingCost = calculateShippingCost();
    const promoDiscount = discount;
    const creditDiscount = appliedCredit;
    
    // Cart page never includes tax
    const finalTotal = Math.max(0, subtotal + giftTotal + shippingCost - promoDiscount - creditDiscount);
    
    console.log('Cart final total calculation (no tax):', { 
      subtotal, 
      shippingCost,
      promoDiscount, 
      creditDiscount, 
      finalTotal
    });
    
    return finalTotal;
  };

  return {
    calculateCartTotal,
    calculateCartCount,
    calculateShippingCost,
    calculateDiscount,
    calculateCreditDiscount,
    calculateFinalTotal,
    calculateCartFinalTotal
  };
};
